import { DEFAULT_AFFINITY, WILDCARD_AFFINITY, getPerson } from './people';
import { stablePick, stableUnit } from './rng';
import {
  CORE_PILLARS,
  ROLES,
  type CorePillar,
  type OutcomeTier,
  type Person,
  type Pillar,
  type Result,
  type Role,
  type Roster,
  type Stats,
} from './types';

/**
 * How much each seat contributes to each pillar. A CTO barely moves
 * fundraising; a Growth hire barely moves engineering. This is why putting the
 * right person in the wrong chair visibly costs you.
 */
const ROLE_WEIGHTS: Record<Role, Record<CorePillar, number>> = {
  CEO: { product: 0.28, engineering: 0.12, growth: 0.28, execution: 0.3, fundraising: 0.4 },
  CTO: { product: 0.16, engineering: 0.45, growth: 0.06, execution: 0.2, fundraising: 0.08 },
  CPO: { product: 0.45, engineering: 0.16, growth: 0.18, execution: 0.16, fundraising: 0.06 },
  COO: { product: 0.1, engineering: 0.1, growth: 0.22, execution: 0.45, fundraising: 0.16 },
  GROWTH: { product: 0.16, engineering: 0.06, growth: 0.45, execution: 0.16, fundraising: 0.18 },
  WILDCARD: { product: 0.2, engineering: 0.2, growth: 0.2, execution: 0.2, fundraising: 0.2 },
};

/** How loudly each seat's personal chaos is felt company-wide. */
const CHAOS_WEIGHT: Record<Role, number> = {
  CEO: 1.4,
  CTO: 0.85,
  CPO: 0.8,
  COO: 1.05,
  GROWTH: 1.1,
  WILDCARD: 1.0,
};

/** A bad fit doesn't just underperform — it generates drama. */
const MISFIT_CHAOS = 55;

const clamp = (value: number, min = 0, max = 100) => Math.min(max, Math.max(min, value));

export const affinityFor = (person: Person, role: Role): number =>
  person.affinity[role] ?? (role === 'WILDCARD' ? WILDCARD_AFFINITY : DEFAULT_AFFINITY);

/**
 * How well a person suits a seat, 0-1. Not shown in the UI — the game keeps the
 * player guessing — but the tuning harness uses it to model skilled play.
 */
export const fitScore = (person: Person, role: Role): number => {
  const weights = ROLE_WEIGHTS[role];
  const raw = CORE_PILLARS.reduce((sum, pillar) => sum + weights[pillar] * person.stats[pillar], 0);
  const total = CORE_PILLARS.reduce((sum, pillar) => sum + weights[pillar], 0);
  return clamp(((raw / total) * affinityFor(person, role)) / 100, 0, 1);
};

export const scorePillars = (roster: Roster): Stats => {
  const picks = ROLES.map((role) => ({ role, pick: roster[role] })).filter(
    (entry): entry is { role: Role; pick: NonNullable<Roster[Role]> } => Boolean(entry.pick),
  );

  const scores = {} as Stats;

  for (const pillar of CORE_PILLARS) {
    let weighted = 0;
    let total = 0;
    for (const { role, pick } of picks) {
      const person = getPerson(pick.personId);
      const weight = ROLE_WEIGHTS[role][pillar];
      weighted += weight * person.stats[pillar] * affinityFor(person, role);
      total += weight;
    }
    scores[pillar] = total > 0 ? clamp(Math.round(weighted / total)) : 0;
  }

  let chaosWeighted = 0;
  let chaosTotal = 0;
  for (const { role, pick } of picks) {
    const person = getPerson(pick.personId);
    const weight = CHAOS_WEIGHT[role];
    const misfit = Math.max(0, 0.9 - affinityFor(person, role)) * MISFIT_CHAOS;
    chaosWeighted += weight * (person.stats.chaos + misfit);
    chaosTotal += weight;
  }
  scores.chaos = chaosTotal > 0 ? clamp(Math.round(chaosWeighted / chaosTotal)) : 0;

  return scores;
};

/** Weighting used to collapse the five pillars into one number. */
const PILLAR_WEIGHTS: Record<CorePillar, number> = {
  product: 0.22,
  engineering: 0.19,
  growth: 0.2,
  execution: 0.21,
  fundraising: 0.18,
};

/** How much the weakest pillar drags the whole company. */
const WEAKEST_LINK_WEIGHT = 0.28;
/** Chaos above this starts costing you; below it, calm competence pays a little. */
const CHAOS_TOLERANCE = 55;
const CHAOS_PENALTY = 34;
const CHAOS_BONUS = 4;

export const compositeScore = (scores: Stats): number => {
  const core = CORE_PILLARS.reduce((sum, pillar) => sum + PILLAR_WEIGHTS[pillar] * scores[pillar], 0);
  const weakest = Math.min(...CORE_PILLARS.map((pillar) => scores[pillar]));
  const balanced = (1 - WEAKEST_LINK_WEIGHT) * core + WEAKEST_LINK_WEIGHT * weakest;

  const drag =
    scores.chaos > CHAOS_TOLERANCE
      ? ((scores.chaos - CHAOS_TOLERANCE) / (100 - CHAOS_TOLERANCE)) ** 1.7 * CHAOS_PENALTY
      : -((CHAOS_TOLERANCE - scores.chaos) / CHAOS_TOLERANCE) * CHAOS_BONUS;

  return clamp(balanced - drag);
};

const B = 1e9;
const T = 1e12;

type TierDef = {
  tier: OutcomeTier;
  /** Minimum composite required. Evaluated highest-first. */
  min: number;
  headlines: string[];
  valuation: [number, number];
  /**
   * Extra conjunctions the roster must satisfy. This is what keeps the top of
   * the table rare: a lopsided team with a huge composite still gets bumped
   * down a tier.
   */
  requires?: { minPillar?: number; maxChaos?: number };
};

/**
 * Thresholds are tuned against `scripts/simulate-outcomes.mjs` (`npm run simulate`) so that a casual
 * draft lands on $1T roughly once every ~300 games. Change one number here and
 * re-run that script before shipping.
 */
const TIERS: TierDef[] = [
  {
    tier: 'trillion',
    min: 88,
    headlines: ['A $1 trillion company', 'Generational, unrepeatable', 'The company of the century'],
    valuation: [1 * T, 2.4 * T],
    requires: { minPillar: 88, maxChaos: 44 },
  },
  {
    tier: 'category',
    min: 86,
    headlines: ['Category-defining company', 'Redefined an entire industry', 'The default choice, everywhere'],
    valuation: [320 * B, 960 * B],
    requires: { minPillar: 82, maxChaos: 62 },
  },
  {
    tier: 'giant',
    min: 81,
    headlines: ['Became a tech giant', 'Too big to be acquired', 'Regulators are now involved'],
    valuation: [110 * B, 320 * B],
  },
  {
    tier: 'ipo',
    min: 74,
    headlines: ['IPO', 'Rang the bell', 'Went public, mostly on purpose'],
    valuation: [22 * B, 110 * B],
  },
  {
    tier: 'decacorn',
    min: 66,
    headlines: ['Decacorn', 'Series F at a scary price', 'Quietly enormous'],
    valuation: [10 * B, 22 * B],
  },
  {
    tier: 'unicorn',
    min: 55,
    headlines: ['Unicorn', 'Reached escape velocity', 'A real, actual business'],
    valuation: [1 * B, 10 * B],
  },
  {
    tier: 'acquired',
    min: 40,
    headlines: ['Acquired by Google', 'Acqui-hired by Meta', 'Absorbed by Microsoft', 'Sold for parts'],
    valuation: [0.04 * B, 1 * B],
  },
  {
    tier: 'dead',
    min: 0,
    headlines: ['Dead on arrival', 'Never shipped', 'Ran out of money in month 9'],
    valuation: [0, 0.04 * B],
  },
];

/** Chaos this high blows the company up regardless of how good it was. */
const IMPLOSION_CHAOS = 80;
const IMPLOSION_SURVIVABLE_COMPOSITE = 76;

const IMPLOSION_HEADLINES = [
  'Imploded spectacularly',
  'Imploded, on a podcast',
  'Collapsed in 11 days',
  'Chapter 11, then a documentary',
];

const weakestPillar = (scores: Stats) => Math.min(...CORE_PILLARS.map((pillar) => scores[pillar]));

const pickTier = (scores: Stats, composite: number): TierDef => {
  if (scores.chaos >= IMPLOSION_CHAOS && composite < IMPLOSION_SURVIVABLE_COMPOSITE) {
    return {
      tier: 'imploded',
      min: 0,
      headlines: IMPLOSION_HEADLINES,
      valuation: [0, 0.6 * B],
    };
  }

  const weakest = weakestPillar(scores);
  for (const tier of TIERS) {
    if (composite < tier.min) continue;
    if (tier.requires?.minPillar !== undefined && weakest < tier.requires.minPillar) continue;
    if (tier.requires?.maxChaos !== undefined && scores.chaos > tier.requires.maxChaos) continue;
    return tier;
  }
  return TIERS[TIERS.length - 1];
};

const valuationFor = (tier: TierDef, composite: number, seed: string): number => {
  const [low, high] = tier.valuation;
  // The floor of the tier directly above this one — TIERS is ordered high to
  // low, so this is the *smallest* min that still beats ours.
  const nextMin = TIERS.reduce(
    (best, t) => (t.min > tier.min ? Math.min(best, t.min) : best),
    Number.POSITIVE_INFINITY,
  );
  const span = Number.isFinite(nextMin) ? nextMin - tier.min : 100 - tier.min;
  const progress = span > 0 ? clamp((composite - tier.min) / span, 0, 1) : 0.5;
  // Bias towards the bottom of the band so top-of-tier numbers stay special.
  const curved = progress ** 1.4;
  const jitter = (stableUnit(`${seed}:valuation`) - 0.5) * 0.16;
  const value = low + (high - low) * clamp(curved + jitter, 0, 1);
  return Math.max(0, value);
};

const STRENGTH_LINES: Record<CorePillar, string[]> = {
  product: [
    'The product is genuinely, upsettingly good.',
    'Every screen looks like it took a year.',
    'Competitors screenshot your app in their board decks.',
  ],
  engineering: [
    'The engineering org is a war machine.',
    'Uptime is a personality trait here.',
    'They rewrote the whole stack in a weekend and it worked.',
  ],
  growth: [
    'Distribution is absurd. It spreads like a rumour.',
    'The growth curve is legally suspicious.',
    'Everyone you know already has an account.',
  ],
  execution: [
    'Ships on time, every time, somehow.',
    'The operating cadence is terrifyingly good.',
    'Roadmaps here actually mean something.',
  ],
  fundraising: [
    'Can raise a Series B in an elevator.',
    'Term sheets arrive unprompted.',
    'Investors are begging to be let in at any price.',
  ],
};

const WEAKNESS_LINES: Record<CorePillar, string[]> = {
  product: ['Nobody can explain what the product does.', 'The product is a settings page with dreams.'],
  engineering: ['The codebase is one intern away from collapse.', 'Everything is down. It is always down.'],
  growth: ['Nobody has heard of it. Nobody will.', 'Growth strategy is currently "hope".'],
  execution: ['Roadmaps are decorative here.', 'Every deadline is aspirational fiction.'],
  fundraising: ['The runway ends before the roadmap does.', 'Investors keep leaving the meeting early.'],
};

const CHAOS_LINES = [
  'Board meetings are absolutely unbearable.',
  'Someone on this cap table will be deposed.',
  'HR has its own HR department.',
  'Three cofounders are not speaking to each other.',
  'The all-hands ended in a shouting match. Again.',
  'Legal has a dedicated Slack channel for this team.',
];

const CALM_LINES = [
  'Remarkably, everyone actually likes each other.',
  'Zero drama. Genuinely unheard of.',
  'The board meeting ended fourteen minutes early.',
];

const summaryFor = (scores: Stats, seed: string): string[] => {
  const ranked = [...CORE_PILLARS].sort((a, b) => scores[b] - scores[a]);
  const best = ranked[0];
  const worst = ranked[ranked.length - 1];

  const first = stablePick(STRENGTH_LINES[best], `${seed}:strength`);
  const second =
    scores.chaos >= 72
      ? stablePick(CHAOS_LINES, `${seed}:chaos`)
      : scores[worst] < 55
        ? stablePick(WEAKNESS_LINES[worst], `${seed}:weak`)
        : scores.chaos <= 30
          ? stablePick(CALM_LINES, `${seed}:calm`)
          : stablePick(CHAOS_LINES, `${seed}:chaos`);

  return [first, second];
};

export const rosterSeed = (roster: Roster): string =>
  ROLES.map((role) => `${role}:${roster[role]?.personId ?? '-'}`).join('|');

/**
 * Pure function of the roster. Same team, same roles → same result, always.
 */
export const simulate = (roster: Roster): Result => {
  const scores = scorePillars(roster);
  const composite = compositeScore(scores);
  const seed = rosterSeed(roster);
  const tier = pickTier(scores, composite);

  return {
    scores,
    composite,
    tier: tier.tier,
    headline: stablePick(tier.headlines, `${seed}:headline`),
    valuation: valuationFor(tier, composite, seed),
    summary: summaryFor(scores, seed),
  };
};

export const formatValuation = (value: number): string => {
  if (value >= T) return `$${(value / T).toFixed(value >= 10 * T ? 1 : 2)}T`;
  if (value >= B) return `$${(value / B).toFixed(value >= 100 * B ? 0 : 1)}B`;
  if (value >= 1e6) return `$${Math.round(value / 1e6)}M`;
  return '$0';
};

export const PILLAR_LABELS: Record<Pillar, string> = {
  product: 'PRODUCT',
  engineering: 'ENGINEERING',
  growth: 'GROWTH',
  execution: 'EXECUTION',
  fundraising: 'FUNDRAISING',
  chaos: 'CHAOS',
};
